<template>
    <div v-if="recognition" class="my-3 flex items-center text-theme-text">
        <span v-if="speaking" role="status" aria-live="polite" class="px-3 text-sm"
            >{{ localizations[lang].listeningSpeech }} ...</span
        >
        <span v-if="isListening && !speaking" role="status" aria-live="polite" class="px-3 text-sm">{{
            localizations[lang].speakNow
        }}</span>
        <button
            class="flatButton"
            type="button"
            :class="{
                'isListening text-white': isListening,
                isSpeaking: speaking,
            }"
            :disabled="error"
            @click.stop="isListening ? endSpeechRecognition() : startSpeechRecognition()"
        >
            <svg-icon :icon="micIcon"></svg-icon>
        </button>
        <p v-if="error" role="alert" aria-live="assertive" class="block text-sm font-medium text-red-600">
            {{ error }}
        </p>
    </div>
</template>

<script>
import { mapState } from 'vuex';

export default {
    props: {
        speechToTextLang: {
            type: String,
            default: 'en-US',
        },
        text: {
            type: [String, null],
            required: true,
        },
    },
    emits: ['update:text'],
    data() {
        return {
            error: false,
            speaking: false,
            isListening: false,
            runtimeTranscription: '',
            sentences: [],
            recognition: false,
            maybeStopSpeech: null,
        };
    },
    computed: {
        ...mapState(['localizations', 'lang']),
        micIcon() {
            return this.isListening ? 'mic_off-24px' : 'mic-24px';
        },
    },
    mounted() {
        this.setSpeechRecognition();
    },
    beforeDestroy() {
        if (!this.recognition) return;
        this.endSpeechRecognition();
        this.stopListeners();
    },
    methods: {
        setSpeechRecognition() {
            const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
            this.recognition = SpeechRecognition ? new SpeechRecognition() : false;

            if (!this.recognition) {
                this.error = 'Speech Recognition is not available on this browser. Please use Chrome or Firefox';
            }
        },
        endSpeechRecognition() {
            this.recognition.stop();
            this.isListening = false;
            this.speaking = false;
        },
        startSpeechRecognition() {
            if (!this.recognition) {
                this.error = 'Speech Recognition is not available on this browser. Please use Chrome or Firefox';
                return false;
            }
            this.isListening = true;
            this.recognition.lang = this.speechToTextLang;
            this.recognition.interimResults = true;
            // console.log('startSpeechRecognition -> start');
            try {
                this.recognition.start();
            } catch (error) {
                console.error(error);
                this.recognition.stop();
            }

            this.recognition.addEventListener('speechstart', event => this.isSpeaking(event), false);
            this.recognition.addEventListener('speechend', event => this.isSpeaking(event), false);
            this.recognition.addEventListener('result', event => this.getResult(event), false);
            // this.recognition.addEventListener('end', this.sendResult(), false);
            this.recognition.addEventListener('end', () => {
                // console.log('voice:update -> text');
                if (this.runtimeTranscription !== '') {
                    this.sentences.push(this.runtimeTranscription);
                    this.$emit('update:text', `${this.text}${this.sentences.slice(-1)[0]} `);
                }
                this.runtimeTranscription = '';
                this.recognition.stop();
                if (this.isListening) {
                    // keep it going.
                    // console.log('voice:update -> keep it going');
                    try {
                        this.recognition.start();
                    } catch (error) {
                        console.error('keep it going:error start()', error);
                    }
                }
            });
        },
        isSpeaking(event) {
            // console.log({
            //     name: 'isSpeaking',
            //     type: event.type === 'speechstart',
            // });
            if (event.type === 'speechstart') {
                clearTimeout(this.maybeStopSpeech);
                this.speaking = true;
            } else {
                this.speaking = false;
                this.maybeStopSpeech = setTimeout(() => {
                    this.endSpeechRecognition();
                }, 5000);
            }
        },
        getResult(event) {
            const text = Array.from(event.results)
                .map(result => result[0])
                .map(result => result.transcript)
                .join('');
            this.runtimeTranscription = text;
        },

        /*
         * @todo Apply then recognize a sentence like `point` or `semicolon` and apply this symbol
         */
        // capitalizeFirstLetter(string) {
        //     return string.charAt(0).toUpperCase() + string.slice(1);
        // },

        /*
         * @todo Pass this function to event and remove it when this component is close
         */
        // sendResult() {
        //     console.log('voice:end -> start');
        //     if (this.runtimeTranscription !== '') {
        //         this.sentences.push(this.runtimeTranscription)
        //         this.$emit('update:text', `${this.text}${this.sentences.slice(-1)[0]} `)
        //     }
        //     this.runtimeTranscription = ''
        //     this.recognition.stop();
        //     if (this.isListening) {
        //         // keep it going.
        //         console.log('voice:end -> keep it going');
        //         this.recognition.start();
        //     } else {
        //         this.recognition.stop();
        //     }
        // },
        stopListeners() {
            this.recognition.removeEventListener('speechstart', event => this.isSpeaking(event), false);
            this.recognition.removeEventListener('speechend', event => this.isSpeaking(event), false);
            this.recognition.removeEventListener('result', event => this.getResult(event), false);
            // this.recognition.removeEventListener('end', this.sendResult(), false);
        },
    },
};
</script>

<style scoped>
.flatButton.isListening > div:before {
    background: red;
    opacity: 1;
}

.flatButton.isListening.isSpeaking > div:before {
    background: red;
    transform: scale(1.4);
}
</style>
